import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from "@mui/material";
import Header from "../../Home/Header";
import Footer from "../../Home/Footer";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { entity_id, getGeolocation, locationGeo, Platform } from "../../Utils/Const";
import { baseapi } from "../../Environmentalurl";
import useIPAddress from "../../Utils/useIPAddress";
import api from "../../Utils/Api";

export default function VerifyOTP() {
  const steps = [
    "Mobile verification",
    "Profile Details",
    "Security Checkup",
    "Complete",
  ];

  const [timer, setTimer] = useState(59);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [canResend, setCanResend] = useState(false);
  const [resending, setResending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { mobile, messageRefId } = location.state || {};
  const [currentRefId, setCurrentRefId] = useState(messageRefId);

  // const [currentRefId, setCurrentRefId] = useState(messageRefId);
  // useEffect(() => {
  //   if (!mobile || !messageRefId) {
  //     console.error("Missing navigation data:", { mobile, messageRefId });
  //   }
  // }, [mobile, messageRefId]);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (timer === 0) {
      setCanResend(true);
      return;
    }

    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const ip = useIPAddress();

  const handleVerify = async () => {
    const enteredOTP = otp.join("");

    if (enteredOTP.length !== 6) {
      setError("Please enter 6 digit OTP");
      return;
    }

    try {
      setError("");
      const payload = {
        ReqType: "MT_Reg_ValidateOTP",
        Mobilenumber: mobile,
        MessageRefID: currentRefId,
        OTP: enteredOTP,
        Platform,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID:ip ,
        DeviceID: "WEB",
        VersionName: "1.5",
        VersionCode: 7,
      };

      const response = await api.post(`${baseapi}/auth/validateotp`, payload);

      if (response.data.Response_Code === "1000") {
        navigate("/registration/lowkyc/profiledetails", {
          state: {
            mobile: location.state.mobile,
          },
        });
      } else {
        setError(response.data.Message || "OTP validation failed");
      }
    } catch {
      setError("Server error. Please try again.");
    }
  };

  const handleResendOTP = async () => {
    try {
      setResending(true);
      setError("");

      const payload = {
        ReqType: "MT_Reg_ResendOTP",
        MessageRefID: currentRefId,
        Platform: Platform,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      };

      const response = await api.post(`${baseapi}/auth/resendotp`, payload);

      if (response.data.Response_Code === "1000") {
        setCurrentRefId(response.data.MessageRefID);
        setTimer(59);
        setCanResend(false);
      } else {
        setError(response.data.Message || "Failed to resend OTP");
      }
    } catch {
      setError("Server error while resending OTP");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Header />

      <Box sx={{ bgcolor: "#f5f7f6", minHeight: "100vh" }}>
        <Container maxWidth="md" sx={{ mt: 6 }}>
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              display: "flex",
              overflow: "hidden",
            }}
          >
            {/* Left Illustration */}
            <Box
              sx={{
                width: "40%",
                bgcolor: "#0aa34f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <Box
                component="img"
                src="/Assets/image.png"
                sx={{ width: "100%", maxWidth: 260 }}
              />
            </Box>

            {/* Right Content */}
            <Box sx={{ width: "60%", p: 5 }}>
              <Typography
                sx={{
                  cursor: "pointer",
                  mb: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  color: "#3d7f45",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/registration/lowkyc")}
              >
                ← Back
              </Typography>

              <Stepper activeStep={0} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Typography variant="h5" fontWeight="bold" mt={4}>
                Enter Verification Code
              </Typography>

              <Typography color="text.secondary" mt={1}>
                We’ve sent a verification code to your phone number
              </Typography>

              <Box display="flex" gap={1} mb={2} mt={2}>
                {otp.map((v, i) => (
                  <TextField
                    key={i}
                    value={v}
                    inputRef={(el) => (inputRefs.current[i] = el)}
                    onChange={(e) => handleChange(e.target.value, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: "center", fontSize: "20px" },
                    }}
                    sx={{ width: 55 }}
                  />
                ))}
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary" fontSize={14}>
                  {canResend ? (
                    "Didn't receive OTP?"
                  ) : (
                    <>
                      Resend code in{" "}
                      <b>00:{timer.toString().padStart(2, "0")}</b>
                    </>
                  )}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    cursor: canResend && !resending ? "pointer" : "not-allowed",
                    color: canResend ? "#3d7f45" : "#aaa",
                    fontWeight: 500,
                  }}
                  onClick={
                    canResend && !resending ? handleResendOTP : undefined
                  }
                >
                  {resending ? "Resending..." : "Resend Code"}
                </Typography>
              </Box>

              <Button
                fullWidth
                onClick={handleVerify}
                sx={{
                  mt: 3,
                  bgcolor: "#0aa34f",
                  py: 1.5,
                  color: "white",
                  fontSize: 16,
                  "&:hover": { bgcolor: "#0aa34f" },
                }}
                variant="contained"
              >
                Verify
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
